import React, { useState, useEffect, useRef } from 'react';
import { 
  YoutubeLogo, 
  Play, 
  ArrowUpRight, 
  Clock,
  Warning
} from '@phosphor-icons/react';
import { useYouTubeVideos, getRelativeTime, formatDate } from '../hooks/useYouTubeVideos';
import './YouTubeSection.css';

const VideoCard = ({ video, index, isActive, onClick }) => {
  const cardRef = useRef(null);

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const handleMouseMove = (e) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      
      const rotateX = (y - centerY) / 20;
      const rotateY = (centerX - x) / 20;
      
      card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-8px) scale(1.02)`;
    };

    const handleMouseLeave = () => {
      card.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) translateY(0) scale(1)';
    };

    card.addEventListener('mousemove', handleMouseMove);
    card.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      card.removeEventListener('mousemove', handleMouseMove);
      card.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <button
      ref={cardRef}
      className={`video-card stagger-item ${isActive ? 'active' : ''}`}
      onClick={onClick}
      style={{ animationDelay: `${0.2 + index * 0.1}s` }}
    >
      <div className="video-card-bg-shape"></div>
      <div className="video-card-thumb">
        <img 
          src={video.thumbnail}
          alt={video.title}
          className="video-card-image"
          loading="lazy"
        />
        <div className="video-card-overlay">
          <Play weight="fill" className="video-card-play-icon" />
        </div>
        <div className="video-card-number">{index + 2}</div>
      </div>
      <div className="video-card-content">
        <h5 className="video-card-title">{video.title}</h5>
        <span className="video-card-time">
          <Clock weight="fill" className="video-card-time-icon" />
          {getRelativeTime(video.publishedAt)}
        </span>
      </div>
    </button>
  );
};

const YouTubeSection = () => {
  const sectionRef = useRef(null);
  const channelUrl = 'https://www.youtube.com/@islamiccentretvBangka';
  
  const { 
    videos, 
    loading, 
    error, 
    featuredVideo,
    playlistVideos 
  } = useYouTubeVideos(5);

  const [activeVideo, setActiveVideo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    if (featuredVideo && !activeVideo) {
      setActiveVideo(featuredVideo);
    }
  }, [featuredVideo, activeVideo]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.stagger-item, .header-animate');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handlePlayVideo = () => {
    if (activeVideo?.videoId) {
      setIsPlaying(true);
    }
  };

  const handleVideoSelect = (video) => {
    setActiveVideo(video);
    setIsPlaying(false);
  };

  const showError = error && !loading;
  const hasVideos = videos.length > 0;

  return (
    <section className="youtube-section" ref={sectionRef}>
      <div className="container-tight">
        {/* Section Header */}
        <div className="youtube-header header-animate">
          <div className="youtube-header-grid">
            <div className="youtube-header-left">
              <span className="youtube-label">ISLAMIC CENTRE TV</span>
              <h2 className="youtube-title">
                Video Terbaru<br/>
                <span className="youtube-title-muted">dari Channel Kami</span>
              </h2>
            </div>
            <div className="youtube-header-right">
              <p className="youtube-description">
                Tonton kajian, acara, dan aktivitas terbaru dari Bahrul Ulum Islamic Centre
              </p>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="youtube-loading-state stagger-item">
            <div className="youtube-loading-spinner-large" />
            <p>Memuat video terbaru dari YouTube...</p>
          </div>
        )}

        {/* Error State */}
        {showError && (
          <div className="youtube-error-state stagger-item">
            <div className="youtube-error-icon-wrapper">
              <Warning weight="fill" className="youtube-error-icon" />
            </div>
            <h3>Tidak dapat memuat video</h3>
            <p>Silakan kunjungi channel YouTube kami langsung untuk melihat video terbaru.</p>
            <a
              href={channelUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="youtube-error-btn"
            >
              <YoutubeLogo weight="fill" />
              Kunjungi Channel
            </a>
          </div>
        )}

        {/* Video Content */}
        {!loading && hasVideos && activeVideo && (
          <>
            {/* Main Player Area */}
            <div className="youtube-player-area stagger-item">
              <div className="youtube-player-wrapper">
                {isPlaying ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${activeVideo.videoId}?autoplay=1&rel=0&modestbranding=1`}
                    title={activeVideo.title}
                    className="youtube-iframe"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <div 
                    className="youtube-thumbnail"
                    onClick={handlePlayVideo}
                  >
                    <img 
                      src={`https://img.youtube.com/vi/${activeVideo.videoId}/maxresdefault.jpg`}
                      alt={activeVideo.title}
                      className="youtube-thumbnail-image"
                      onError={(e) => {
                        e.target.src = `https://img.youtube.com/vi/${activeVideo.videoId}/hqdefault.jpg`;
                      }}
                    />
                    <div className="youtube-thumbnail-overlay" />
                    <button className="youtube-play-btn">
                      <Play weight="fill" className="youtube-play-btn-icon" />
                    </button>
                    <div className="youtube-video-badge">
                      <YoutubeLogo weight="fill" />
                      <span>YouTube</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Video Info */}
              <div className="youtube-video-details">
                <h3 className="youtube-video-title-main">{activeVideo.title}</h3>
                <div className="youtube-video-meta">
                  {activeVideo.publishedAt && (
                    <span className="youtube-video-time">
                      <Clock weight="fill" />
                      {getRelativeTime(activeVideo.publishedAt)}
                    </span>
                  )}
                  <span className="youtube-video-date">
                    {formatDate(activeVideo.publishedAt)}
                  </span>
                </div>
                <p className="youtube-video-description">{activeVideo.description}</p>
                <a
                  href={`https://youtube.com/watch?v=${activeVideo.videoId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="youtube-watch-btn"
                >
                  <ArrowUpRight weight="bold" />
                  Tonton di YouTube
                </a>
              </div>
            </div>

            {/* Video Playlist Grid */}
            {playlistVideos.length > 0 && (
              <div className="youtube-playlist-section">
                <div className="youtube-playlist-header stagger-item">
                  <h4>Video Lainnya</h4>
                  <a 
                    href={channelUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="youtube-view-all"
                  >
                    Lihat Semua
                    <ArrowUpRight weight="bold" />
                  </a>
                </div>
                
                <div className="youtube-playlist-grid">
                  {playlistVideos.map((video, index) => (
                    <VideoCard
                      key={video.id}
                      video={video}
                      index={index}
                      isActive={activeVideo.id === video.id}
                      onClick={() => handleVideoSelect(video)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Channel CTA */}
            <div className="youtube-channel-cta stagger-item">
              <div className="youtube-channel-info">
                <div className="youtube-channel-avatar">
                  <YoutubeLogo weight="fill" />
                </div>
                <div className="youtube-channel-text">
                  <h4>Islamic Centre TV</h4>
                  <p>@islamiccentretvBangka</p>
                </div>
              </div>
              <a
                href={channelUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="youtube-subscribe-btn-large"
              >
                <YoutubeLogo weight="fill" />
                Subscribe Channel
              </a>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default YouTubeSection;
